import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as SearchIcon } from "assets/img/inbox/search.svg";
import { Avatar } from "components/elements";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";

interface NewConversationProps {
    setIsOpen?: (isOpen: boolean) => void;
}

export const NewConversation = ({ setIsOpen }: NewConversationProps) => {
    const currentUser = useCurrentUser();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const queryClient = trpc.useContext();

    const createOrGetConversation = trpc.messages.createOrGetConversation.useMutation({
        onSuccess: () => {
            queryClient.messages.getConversations.invalidate();
        },
    });

    const createConversation = async (userId: string) => {
        try {
            const data = await createOrGetConversation.mutateAsync({ userId });
            return (data as { id: string }).id;
        } catch (error) {
            console.error("Failed to create/get conversation:", error);
            throw error;
        }
    };

    const handleSelectUser = async (user: any) => {
        try {
            const conversationId = await createConversation(user.id.toString());
            navigate({ to: "/messages/$id", params: { id: conversationId } });
            setIsOpen && setIsOpen(false);
        } catch (error) {
            console.error("Failed to create conversation:", error);
        }
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setSearchTerm("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const filteredUsers = currentUser?.friends?.filter((user) => {
        return user.name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <div
            className="relative flex w-full flex-col items-center justify-center"
            ref={dropdownRef}
        >
            <div className="relative mb-2 w-full">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                    type="text"
                    placeholder="Search users..."
                    className="w-full rounded-md border-none bg-[#eeeeee] py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-0 focus:ring-slate-100"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div
                className={`
                    mt-3 max-h-64 w-full overflow-auto rounded-2xl bg-[#FAFFFE] shadow-none
                    ${searchTerm.trim().length > 0 && filteredUsers && filteredUsers.length > 0 ? "border border-[#d5d5d5]" : ""}
                `}
            >
                {searchTerm.trim().length > 0 ? (
                    filteredUsers && filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                            <div
                                key={user.id}
                                className="flex cursor-pointer items-center gap-3 border-b border-[#d5d5d5] p-3 last:border-b-0 hover:bg-gray-100"
                                onClick={() => handleSelectUser(user)}
                            >
                                <Avatar
                                    className="h-[46px] w-[46px]"
                                    photoUrl={user.avatarUrl}
                                    name={user.name}
                                />
                                <span className="text-gray-900">{user.name}</span>
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-4 text-center text-gray-500">No users found</div>
                    )
                ) : null}
            </div>
        </div>
    );
};
